import React, { useCallback, useEffect, useState } from "react";
import HeaderImage from "components/HeaderImage/HeaderImage";
import EventsImage from "assets/images/headerImage/EventsImage.png";
import SearchBar from "components/SearchBar/SearchBar";
import { SpinLoad } from "components/Spin/SpinLoad";
import { Row, Col, Empty, List, Card, Typography, Checkbox, Button } from "antd";
import axios from "axios";
import { Link } from "react-router-dom";
import SingleEventBox from "containers/Events/SingelEventBox/SingleEventBox";
import EventFilterComponent from "containers/Events/EventFilter/EventFilter";
import dayjs from "dayjs";
import { Event } from "interfaces/events";
import { useLoginContext, isCompanyUser } from "contexts/LoginContext/LoginContext";
import { getFilteredEvents, getEventStatusMessage } from "./helpers";

const Events = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [sectorsChecked, setSectorChecked] = useState<number[]>([]);
  const [ownEvents, setOwnEvents] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  const { currentUser } = useLoginContext();

  const toggleCheckedSectors = useCallback(
    (id: number) =>
      setSectorChecked(previousState => {
        if (previousState.includes(id)) return previousState.filter(x => x !== id);
        return [...previousState, id];
      }),
    []
  );

  const toggleOwnEvents = () => setOwnEvents(!ownEvents); // Companies only see own events

  const handleSearch = (newSearchTerm: React.SetStateAction<string>) => {
    setSearchTerm(newSearchTerm);
  };

  useEffect(() => {
    axios
      .get<Event[]>("/events/")
      .then(response => {
        setEvents(response.data.filter(event => event.is_visible === 1 || event.is_visible === 3)); // Only show visible events (BL members get all events returned, so this is just to avoid confusion for them)
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const AxiosEvents = getFilteredEvents(events, searchTerm, sectorsChecked).map(event => {
    if (event.is_visible === 0 || event.is_visible === 2) return null; // 0: Not visible, 3: Only visible in app
    if (ownEvents && event.company.id !== currentUser?.id) return null;

    const getTime = () => {
      if (!event.start) return "Ikke bestemt";
      return dayjs(event.start).format("HH:mm");
    };

    return (
      <Col key={event.id} style={{ marginBottom: "1em" }}>
        <SingleEventBox
          key={event.id}
          id={event.id}
          title={event.name}
          location={event.location}
          time={getTime()}
          date={event.start}
          company={event.company}
        />
      </Col>
    );
  });

  return (
    <>
      <HeaderImage imageLink={EventsImage} alt="EventsImage" header="Arrangementer" />
      <Row justify="center" style={{ paddingLeft: "5%", paddingRight: "5%", marginTop: "40px", marginBottom: "40px" }}>
        <Col xs={24} sm={24} md={19} lg={19}>
          <SearchBar value={searchTerm} handleSearch={handleSearch} placeholder="Søk..." />
        </Col>
      </Row>
      <Row style={{ paddingLeft: "5%", paddingRight: "5%" }}>
        <Col xs={24} sm={24} md={6} lg={6}>
          {isCompanyUser(currentUser) ? (
            <Card bodyStyle={{ padding: "2em" }} style={{ marginBottom: "1em" }}>
              <Row gutter={[0, 16]}>
                <Col span={24}>
                  <Link to="/company/event-booking/">
                    <Button style={{ background: "var(--secondary-color)", color: "var(--light)", width: "100%" }}>
                      Book arrangement
                    </Button>
                  </Link>
                </Col>
                <Col span={24}>
                  <Checkbox checked={ownEvents} onChange={() => toggleOwnEvents()} value={ownEvents}>
                    <Typography>Vis kun mine arrangementer</Typography>
                  </Checkbox>
                </Col>
              </Row>
            </Card>
          ) : null}
          <EventFilterComponent
            toggleCheckedSectors={toggleCheckedSectors}
            sectorsChecked={sectorsChecked}
            onFilterReset={() => {
              setSectorChecked([]);
              handleSearch("");
            }}
          />
        </Col>
        <Col xs={24} sm={24} md={1} lg={1} style={{ minHeight: "2em" }} />
        <Col xs={24} sm={24} md={17} lg={17}>
          <List
            itemLayout="vertical"
            pagination={{
              pageSize: 20,
              responsive: true,
              defaultCurrent: 1,
              defaultPageSize: 50,
              pageSizeOptions: ["20", "50"],
              hideOnSinglePage: true,
            }}
            locale={{
              emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={getEventStatusMessage(events)} />,
            }}
            dataSource={AxiosEvents.filter(event => event)} /* Can be null so added this check */
            renderItem={item => item}
            loading={{ indicator: <SpinLoad />, spinning: loading }}
          />
          <br />
        </Col>
      </Row>
    </>
  );
};

export default Events;
